import React, { useRef, useReducer, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Hls from 'hls.js';
import { Box, makeStyles, Typography } from '@material-ui/core';

import GroupShowStore from '../../store/GroupShowStore';

import reducer, { initialState, PLAYBACK_STATE, ACTION_TYPE } from './reducer';


const PROVIDER_TYPE = {
  STREAMING_SAAS: 'STREAMING_SAAS',
  WOWZA: 'WOWZA',
  CAM4_STREAMING_SAAS: 'CAM4_STREAMING_SAAS',
  EXTERNAL: 'EXTERNAL',
};

const PROTOCOL = {
  HLS: 'HLS',
};

const useStyles = makeStyles((theme) => {
  return {
    flagIcon: {
      backgroundColor: '#FF0000',
      position: 'absolute',
      top: 30,
      right: 10,
      borderRadius: '10px',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2px 10px',
      '& > p': {
        fontSize: '12px',
        fontWeight: 700,
      },
      [theme.breakpoints.down('sm')]: {
        top: 90,
        right: 15,
      },
    },
  };
});

function HLSStreamViewPlayer(props) {
  const [state, setState] = useReducer(reducer, initialState);
  const classes = useStyles();
  const {
    videoId,
    controls,
    videoClass,
    loadingOverlay,
    autoplay,
    onHLSError,
    streamData,
    gsHost,
    isInGroupShow,
    hasStreamViews,
    toggleStreamLoading,
    isTulumRealityShow,
    onAllowStreamChangeOnError,
    onLiveSessionEnd,
  } = props;

  const videoRef = useRef();
  const player = useRef();

  useEffect(() => {
    if (isTulumRealityShow && !streamData?.id) {
      const playerRef = player.current;
      if (playerRef?.stopLoad) {
        player.current.stopLoad();
      }
    }
  }, [isTulumRealityShow, streamData]);

  useEffect(() => {
    if (player.current || videoRef.current) {
      stopHLS(true);
    }
  }, [stopHLS, streamData]);

  useEffect(
    () => {
      if ((autoplay && (hasStreamViews || (isTulumRealityShow && streamData?.url))) || isInGroupShow) {
        playStream();
      }

      if (player.current && !autoplay) {
        stopStream();
      }

      const playerRef = player?.current;

      return () => {
        if (playerRef && typeof (playerRef.stop) === 'function') {
          playerRef.stop();
        }
        if (playerRef && typeof (playerRef.stopLoad) === 'function') {
          playerRef.stopLoad();
        }
      };
    },
    [autoplay, hasStreamViews, isInGroupShow, playStream, stopOngoingStream, stopStream]
  );

  const stopOngoingStream = useCallback(() => {
    if ((streamData.provider === PROVIDER_TYPE.WOWZA || streamData.provider === PROVIDER_TYPE.EXTERNAL) && player.current) {
      stopHLS();
    }
  }, [stopHLS, streamData.provider]);

  useEffect(() => {
    if (gsHost && isInGroupShow) {
      GroupShowStore.setHostVideoRef(videoRef);
    }

    if (!gsHost && isInGroupShow) {
      GroupShowStore.setParticipantVideoRef(videoRef);
    }
  }, [gsHost, isInGroupShow]);

  const dispatch = (type, payload) => setState({ type, payload });

  const playStream = useCallback(() => {

    if (streamData.provider === PROVIDER_TYPE.EXTERNAL) {
      startHLS(streamData);
    }

    if (streamData.provider === PROVIDER_TYPE.WOWZA) {
      const hls = streamData?.streamViews?.[0];
      if (hls) {
        startHLS(hls);
      }
    } else {
      const hls = streamData?.streamViews?.find(
        (item) => item.protocol === PROTOCOL.HLS
      );
      if (hls) {
        startHLS(hls);
      }
    }
  }, [startHLS, streamData.provider, streamData?.streamViews, streamData?.url]);

  const stopStream = useCallback(() => {
    stopHLS();
  }, [state.activeProtocol, stopHLS]);

  const onStopped = useCallback(() => {
    dispatch(ACTION_TYPE.SET_PLAYBACK_STATE, PLAYBACK_STATE.STOPPED);
  }, []);

  const startHLS = useCallback((hls) => {
    dispatch(ACTION_TYPE.SET_PLAYBACK_STATE, PLAYBACK_STATE.STARTING);
    dispatch(ACTION_TYPE.SET_ACTIVE_PROTOCOL, PROTOCOL.HLS);
    const { url, accessToken } = hls || {};
    if (player.current) {
      stopHLS();
    }

    if (hls && url && videoRef.current) {
      const streamUrl = accessToken ? `${url}?id=${accessToken}` : url;

      if (Hls.isSupported()) {
        videoRef.current.muted = state.muted;
        player.current = new Hls();
        player.current.loadSource(streamUrl);
        player.current.attachMedia(videoRef.current);
        player.current.on(Hls.Events.ERROR, (data, error) => {
          if (isTulumRealityShow) {
            if (error.fatal) {
              switch (error.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                  player.current.startLoad();
                  onAllowStreamChangeOnError();
                  break;
                case Hls.ErrorTypes.MEDIA_ERROR:
                  player.current.recoverMediaError();
                  onAllowStreamChangeOnError();
                  break;
                default:
                  player.current.destroy();
                  onHLSError(error);
                  break;
              }
            }
          } else {
            onHLSError(error);
          }
        });
        player.current.on(Hls.Events.MANIFEST_PARSED, () => {
          videoRef.current.play().then(() => {
            if (toggleStreamLoading) {
              toggleStreamLoading(false);
            }
            dispatch(ACTION_TYPE.SET_PLAYBACK_STATE, PLAYBACK_STATE.PLAYING);
          }).
            catch(() => {
              onStopped();
            });
        });
      } else if (
        videoRef.current.canPlayType('application/vnd.apple.mpegurl')
      ) {
        videoRef.current.muted = state.muted;
        videoRef.current.src = streamUrl;
        videoRef.current.addEventListener('loadedmetadata', () => {
          videoRef.current.addEventListener('loadeddata', () => {
            props.handleStreamEvent({ status: 'SUCCESS' });
            dispatch(ACTION_TYPE.SET_PLAYBACK_STATE, PLAYBACK_STATE.PLAYING);

          });
          videoRef.current.play()
            .catch((err) => {
              if (err.name !== 'AbortError') {
                props.handleStreamEvent({}, err);
              }
            });
        });
        videoRef.current.addEventListener('error', (err) => {
          if (err.name !== 'AbortError') {
            props.handleStreamEvent({}, err);
          }
        });
      }
    }
  }, [isTulumRealityShow, onAllowStreamChangeOnError, onHLSError, onStopped, state.muted, streamData.provider, toggleStreamLoading]);

  const stopHLS = useCallback((shouldLoadNextCamera) => {
    videoRef.current.pause();
    videoRef.current.src = '';

    if (player.current && typeof (player.current.stopLoad) === 'function') {
      player.current.stopLoad();
      if (typeof (onLiveSessionEnd) === 'function') {
        onLiveSessionEnd();
      }
    }
    player.current = null;

    if (shouldLoadNextCamera) {
      playStream();
    }
  }, [onLiveSessionEnd, playStream]);

  const onMuteChange = useCallback(() => {
    dispatch(ACTION_TYPE.SET_MUTE);
    videoRef.current.muted = !state.muted;
  }, [state.muted]);

  const onVolumeChange = (volume) => {
    dispatch(ACTION_TYPE.SET_VOLUME, volume);
    videoRef.current.volume = volume;
  };

  return (
    <React.Fragment>
      <video
        ref={videoRef}
        autoPlay={true}
        className={videoClass}
        id={videoId}
        muted={state.muted}
        playsInline={true}
      />
      {streamData.isFeatured && (
        <Box className={classes.flagIcon}>
          <Typography>FEATURED</Typography>
        </Box>
      )}
      {loadingOverlay &&
        loadingOverlay(state.playbackState === PLAYBACK_STATE.STARTING)}
      {controls &&
        controls({
          muted: state.muted,
          volume: state.volume,
          showPlay: state.playbackState === PLAYBACK_STATE.STOPPED,
          disableMute: state.playbackState === PLAYBACK_STATE.STARTING,
          startHLS,
          stopStream,
          playStream,
          onMuteChange,
          onVolumeChange,
        })}
    </React.Fragment>
  );
}

HLSStreamViewPlayer.propTypes = {
  autoPlay: PropTypes.bool,
  videoId: PropTypes.string,
  controls: PropTypes.func,
  videoClass: PropTypes.string,
  loadingOverlay: PropTypes.func,
  autoplay: PropTypes.bool,
  onHLSError: PropTypes.func,
  user: PropTypes.object,
  streamData: PropTypes.shape({
    provider: PropTypes.oneOf([
      PROVIDER_TYPE.STREAMING_SAAS,
      PROVIDER_TYPE.WOWZA,
      PROVIDER_TYPE.CAM4_STREAMING_SAAS,
    ]),
  }),
};

HLSStreamViewPlayer.defaultProps = { autoplay: true };

export default HLSStreamViewPlayer;
